/* Shared lifecycle for category, review and USP sections. */
(() => {
  if (window.krSectionSlidersLoaded) return;
  window.krSectionSlidersLoaded = true;
  const controllers = new Map();

  function create(root) {
    const usp = root.matches('.usp-container');
    const category = root.matches('.categories-carousel');
    const items = [...root.querySelectorAll(usp ? '.usp-item' : category ? '.category-item' : '.testimonial-item')];
    const track = root.querySelector(usp ? '.usp-track' : category ? '.carousel-track' : '.testimonial-track');
    const dots = root.querySelector(usp ? '.usp-dots' : category ? '.carousel-dots' : '.slider-dots');
    const viewport = category ? root.querySelector('.carousel-container') : root;
    if (!items.length || !track || !dots || !viewport) return () => {};

    const events = new AbortController();
    const { signal } = events;
    const mobile = matchMedia('(max-width: 768px)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const delay = Math.max(1000, Number(root.dataset.delay) * 1000 || 5000);
    let page = 0, perView = 1, pages = 1, offsets = [0];
    let timer, frame, visible = false, hovered = false, focused = false;
    let destroyed = false;

    function scheduleRotation() {
      clearInterval(timer);
      if (!category && pages > 1 && (!usp || mobile.matches) && visible && !document.hidden && !hovered && !focused && !reducedMotion.matches) {
        timer = setInterval(() => show(page + 1), delay);
      }
    }
    function show(index) {
      page = (index + pages) % pages;
      if (usp) {
        items.forEach((item, i) => item.classList.toggle('active', i === page));
      } else {
        track.style.transform = `translateX(-${offsets[page] || 0}px)`;
      }
      [...dots.children].forEach((dot, i) => {
        dot.classList.toggle('active', i === page);
        dot.setAttribute('aria-current', i === page ? 'true' : 'false');
      });
      scheduleRotation();
    }
    function layout() {
      if (destroyed) return;
      const firstVisible = page * perView;
      perView = usp ? 1 : category
        ? Math.max(1, Number(mobile.matches ? root.dataset.mobileShow : root.dataset.desktopShow) || 1)
        : mobile.matches ? 1 : 3;
      pages = Math.max(1, Math.ceil(items.length / perView));
      page = Math.min(pages - 1, Math.floor(firstVisible / perView));
      if (category) {
        const width = Math.max(1, viewport.clientWidth / perView - 20);
        items.forEach(item => {
          item.style.flex = `0 0 ${width}px`;
          item.style.maxWidth = `${width}px`;
          item.style.margin = '0 10px';
        });
      }
      if (!usp) {
        // Measure after sizing, once per resize. Navigation reuses these offsets.
        const start = items[0].offsetLeft;
        const limit = Math.max(0, track.scrollWidth - viewport.clientWidth);
        offsets = Array.from({length: pages}, (_, i) => Math.min(limit, Math.max(0, items[i * perView].offsetLeft - start)));
      }
      if (dots.children.length !== pages || dots.firstElementChild?.tagName !== 'BUTTON') {
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < pages; i++) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = usp ? 'usp-dot' : 'dot';
          button.dataset.index = i;
          button.setAttribute('aria-label', `Gå til ${usp ? 'fordel' : category ? 'kategoriside' : 'anmeldelsesside'} ${i + 1}`);
          fragment.appendChild(button);
        }
        dots.replaceChildren(fragment);
      }
      root.querySelectorAll(category ? '.carousel-prev, .carousel-next' : '.prev-button, .next-button').forEach(button => {button.disabled = pages <= 1;});
      show(page);
    }
    function resize() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(layout);
    }
    dots.addEventListener('click', event => {
      const button = event.target.closest('button[data-index]');
      if (button && dots.contains(button)) show(Number(button.dataset.index));
    }, {signal});
    root.querySelector(category ? '.carousel-prev' : '.prev-button')?.addEventListener('click', () => show(page - 1), {signal});
    root.querySelector(category ? '.carousel-next' : '.next-button')?.addEventListener('click', () => show(page + 1), {signal});
    root.addEventListener('mouseenter', () => {hovered = true; scheduleRotation();}, {signal});
    root.addEventListener('mouseleave', () => {hovered = false; scheduleRotation();}, {signal});
    root.addEventListener('focusin', () => {focused = true; scheduleRotation();}, {signal});
    root.addEventListener('focusout', event => {focused = root.contains(event.relatedTarget); scheduleRotation();}, {signal});
    document.addEventListener('visibilitychange', scheduleRotation, {signal});
    reducedMotion.addEventListener('change', scheduleRotation, {signal});
    mobile.addEventListener('change', resize, {signal});
    let sizeObserver, visibilityObserver;
    if ('ResizeObserver' in window) {
      let previousWidth;
      sizeObserver = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        if (width !== previousWidth) {previousWidth = width; resize();}
      });
      sizeObserver.observe(viewport);
    } else window.addEventListener('resize', resize, {signal});
    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver(entries => {visible = entries[0].isIntersecting; scheduleRotation();});
      visibilityObserver.observe(root);
    } else visible = true;
    layout();
    return () => {
      destroyed = true;
      events.abort(); clearInterval(timer); cancelAnimationFrame(frame);
      sizeObserver?.disconnect(); visibilityObserver?.disconnect();
    };
  }
  function init(scope) {
    scope.querySelectorAll('.usp-container, .testimonial-slider, .categories-carousel').forEach(root => {
      if (!controllers.has(root)) controllers.set(root, create(root));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), {once:true});
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((destroy, root) => {
      if (event.target.contains(root)) {destroy(); controllers.delete(root);}
    });
  });
})();
